// API client de la Memoria Gobernada (U-CogNet) — habla con el proxy de Orión
// (/orion/api/memory/*), que deriva org_id/access_level/viewer del usuario
// autenticado (NUNCA del navegador) y reenvía al cerebro soberano. Mismo patrón
// de auth (Bearer de localStorage) que client-radar.

const ORION = '/orion/api';
const BASE = '/memory';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

// ── Tipos del contrato (GET /memory/graph) ──
export type Clasif = 'publica' | 'interna' | 'restringida' | 'confidencial';
export type GovStatus = 'approved' | 'vetoed' | 'unknown';

export interface Governance {
  status: GovStatus;
  veto_module: string | null;
}

export interface MemoryNode {
  id: string;
  type: 'org' | 'memory' | 'document' | 'conversation' | 'person' | 'decision';
  label: string;
  clasif?: Clasif;
  source_type?: 'document' | 'conversation' | 'correction' | 'unknown';
  fed_by?: string;
  doc_ref?: string;
  ingested_at?: string;
  decision_id?: string;
  occurrences?: number;
  last_seen?: string;
  governance?: Governance;
  may_annotate?: boolean;
  cluster_id?: number;   // presente con ?similarity=1 (componente conexa de similitud)
}

export interface MemoryEdge {
  source: string;
  target: string;
  rel: 'belongs_to' | 'extracted_from' | 'spoken_in' | 'fed_by' | 'governed_by' | 'corrects' | 'similar_to';
  score?: number;        // coseno 0–1, solo en aristas similar_to
}

export interface GraphMeta {
  org_id: string;
  viewer: string;
  access_level: number;
  include_shared: boolean;
  counts: {
    memories: number;
    distinct_memories: number;
    nodes: number;
    edges: number;
    by_source_type: Record<string, number>;
    by_clasif: Record<string, number>;
    by_governance: Record<string, number>;
  };
  redacted: Record<string, boolean | number>;
  time_range?: { first: string; last: string };
  content_sha256: string;
  similarity?: {
    threshold: number;
    topk: number;
    similar_edges: number;
    clusters: number;
    considered: number;
    capped_from: number;
  };
}

export interface GraphResponse {
  meta: GraphMeta;
  nodes: MemoryNode[];
  edges: MemoryEdge[];
}

export interface AnnotateResult {
  applied: boolean;
  allowed: boolean;
  withheld: boolean;
  veto_module: string | null;
  reason: string | null;
  decision_id: string | null;
  edit_kind: string;
  annotates: string | null;
  memory_written: boolean;
}

export class MemoryApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'MemoryApiError';
    this.status = status;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${ORION}${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(init?.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new MemoryApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
  }
  return body as T;
}

export interface IngestResult {
  chunks: number;
  applied: number;
  per_chunk: { index: number; applied: boolean; veto_module: string | null; decision_id: string }[];
}

export const memoryApi = {
  // redaction/include_shared/similarity son de display (no de seguridad) → pueden
  // ir del cliente. org_id/access_level/viewer NO se mandan: los fija el backend.
  getGraph: (opts?: {
    redaction?: 'none' | 'exists' | 'count';
    includeShared?: boolean;
    similarity?: boolean;
    simThreshold?: number;
  }) => {
    const qs = new URLSearchParams({
      redaction: opts?.redaction ?? 'count',
      include_shared: opts?.includeShared ? '1' : '0',
    });
    if (opts?.similarity) {
      qs.set('similarity', '1');
      qs.set('sim_threshold', String(opts.simThreshold ?? 0.75));
    }
    return req<GraphResponse>(`/graph?${qs.toString()}`);
  },

  // Alimenta un documento (texto ya extraído). El cerebro lo chunkea y gobierna.
  ingest: (body: { doc_ref: string; text: string; clasif: Clasif; chunk_chars?: number }) =>
    req<IngestResult>('/ingest', { method: 'POST', body: JSON.stringify(body) }),

  annotate: (body: {
    clasif: Clasif;
    annotation: string;
    target_decision_id?: string;
    edit_kind: 'correct' | 'augment' | 'redact_request';
  }) => req<AnnotateResult>('/annotate', { method: 'POST', body: JSON.stringify(body) }),
};
